import { Component, OnInit } from '@angular/core';
import { ApprovalPoliciesService, ApprovalPoliciesResponse } from 'src/app/services/approval-policies.service';
import * as alertifyjs from 'alertifyjs';

@Component({
  selector: 'app-approval-policies',
  templateUrl: './approval-policies.component.html',
  styleUrls: ['./approval-policies.component.css']
})
export class ApprovalPoliciesComponent implements OnInit {

  // Estado de la aplicación
  approvalPolicies: ApprovalPoliciesResponse | null = null;
  loading = false;
  error: string | null = null;

  // Control de pestañas activas top-level (4 tabs unificadas Fase 2)
  activeTab: 'motor-decision' | 'mt-scoring' | 'credolab' | 'history' = 'motor-decision';

  // Sub-tabs internas
  activeMtSubTab: 'groups' | 'conditions' = 'groups';
  activeCredolabSubTab: 'thresholds' | 'flags' = 'thresholds';

  // Estado del historial
  auditLogs: any[] = [];
  loadingLogs = false;
  
  // Filtros del historial
  historyFilters = {
    code: '',
    dateInitial: '',
    dateFinal: ''
  };

  constructor(private approvalPoliciesService: ApprovalPoliciesService) {}

  ngOnInit(): void {
    this.loadApprovalPolicies();
  }

  /**
   * Cargar políticas de aprobación desde la API
   */
  loadApprovalPolicies = async () => {
    this.loading = true;
    this.error = null;

    try {
      const response = await this.approvalPoliciesService.getApprovalPolicies();
      this.approvalPolicies = response.data;
      console.log('Políticas de aprobación cargadas:', this.approvalPolicies);
    } catch (error) {
      console.error('Error cargando políticas de aprobación:', error);
      this.error = 'Error al cargar las políticas de aprobación';
      alertifyjs.error('Error al cargar las políticas de aprobación');
    } finally {
      this.loading = false;
    }
  };

  /**
   * Cambiar pestaña activa top-level
   */
  setActiveTab(tab: 'motor-decision' | 'mt-scoring' | 'credolab' | 'history'): void {
    this.activeTab = tab;

    // Cargar logs cuando se activa la pestaña de historial
    if (tab === 'history' && this.auditLogs.length === 0) {
      this.loadAuditLogs();
    }
  }

  setActiveMtSubTab(sub: 'groups' | 'conditions'): void {
    this.activeMtSubTab = sub;
  }

  setActiveCredolabSubTab(sub: 'thresholds' | 'flags'): void {
    this.activeCredolabSubTab = sub;
  }

  /**
   * Manejar actualizaciones de políticas (para refrescar datos)
   */
  onPoliciesUpdated = () => {
    this.loadApprovalPolicies();
  };

  /**
   * Verificar si hay datos cargados
   */
  hasData(): boolean {
    return this.approvalPolicies !== null;
  }

  /**
   * Cargar logs de auditoría desde la API
   */
  loadAuditLogs = async () => {
    this.loadingLogs = true;

    try {
      // Convertir fechas de formato input (yyyy-mm-dd) a formato API (mm/dd/yyyy)
      let dateInitialFormatted = '';
      let dateFinalFormatted = '';

      if (this.historyFilters.dateInitial) {
        const [year, month, day] = this.historyFilters.dateInitial.split('-');
        dateInitialFormatted = `${month}/${day}/${year}`;
      }

      if (this.historyFilters.dateFinal) {
        const [year, month, day] = this.historyFilters.dateFinal.split('-');
        dateFinalFormatted = `${month}/${day}/${year}`;
      }

      console.log('🔍 DEBUG Frontend: Parámetros enviados a la API:', {
        code: this.historyFilters.code,
        dateInitial: dateInitialFormatted,
        dateFinal: dateFinalFormatted
      });

      const response = await this.approvalPoliciesService.getAuditLogs(
        this.historyFilters.code,
        dateInitialFormatted,
        dateFinalFormatted
      );
      
      console.log('Respuesta del servicio:', response);
      
      // Agrupar logs por código
      const rawLogs = response.data || [];
      this.auditLogs = this.groupLogsByCode(rawLogs);
      
      console.log('Logs de auditoría agrupados:', this.auditLogs);
    } catch (error) {
      console.error('Error cargando logs de auditoría:', error);
      alertifyjs.error('Error al cargar el historial de auditoría');
    } finally {
      this.loadingLogs = false;
    }
  };

  /**
   * Aplicar filtros del historial
   */
  applyHistoryFilters = () => {
    this.loadAuditLogs();
  };

  /**
   * Limpiar filtros del historial
   */
  clearHistoryFilters = () => {
    this.historyFilters = {
      code: '',
      dateInitial: '',
      dateFinal: ''
    };
    this.loadAuditLogs();
  };

  /**
   * Formatear fecha para mostrar en el historial
   */
  formatDate(date: string): string {
    return date || 'Sin fecha';
  }

  /**
   * Obtener etiqueta de código para el historial
   */
  getCodeLabel(code: string): string {
    const labels: { [key: string]: string } = {
      'AGE_POLITICS': 'Política de Edad (Deprecated)',
      'GROUP_AGE_POLICY_UPDATE': 'Límites de Edad de Grupo Actualizados',  // NUEVO
      'ADDED_WORK_CONDITION': 'Condición Laboral Agregada',
      'ADDED_WORK_CONDITION_TO_GROUP': 'Condición Agregada a Grupo',
      'REMOVED_WORK_CONDITION_FROM_GROUP': 'Condición Removida de Grupo',
      'CREATED_APPROVAL_GROUP': 'Grupo de Aprobación Creado',
      'UPDATED_APPROVAL_GROUP_INCOME_LIMITS': 'Límites de Ingresos Actualizados',
      'DELETED_APPROVAL_GROUP': 'Grupo de Aprobación Eliminado',
      'ADDED_WORK_CONDITION_TO_REFUSED_LIST': 'Condición Agregada a Lista Rechazada',
      'REMOVED_WORK_CONDITION_FROM_REFUSED_LIST': 'Condición Removida de Lista Rechazada',
      'APPROVAL_POLICY_MODE_UPDATE': 'Modo de política actualizado',
      'CREDOLAB_THRESHOLDS_UPDATE': 'Umbrales Credolab actualizados',
      'CREDOLAB_FLAG_UPDATE': 'Flag Credolab actualizado'
    };
    return labels[code] || code;
  }

  /**
   * Obtener color asociado a un código
   */
  getCodeColor(code: string): string {
    const colors: { [key: string]: string } = {
      'AGE_POLITICS': '#6c757d',  // Gris para deprecated
      'GROUP_AGE_POLICY_UPDATE': '#007bff',  // NUEVO: Azul para edad por grupo
      'ADDED_WORK_CONDITION': '#28a745',
      'ADDED_WORK_CONDITION_TO_GROUP': '#17a2b8',
      'REMOVED_WORK_CONDITION_FROM_GROUP': '#ffc107',
      'CREATED_APPROVAL_GROUP': '#6f42c1',
      'UPDATED_APPROVAL_GROUP_INCOME_LIMITS': '#fd7e14',
      'DELETED_APPROVAL_GROUP': '#dc3545',
      'ADDED_WORK_CONDITION_TO_REFUSED_LIST': '#e83e8c',
      'REMOVED_WORK_CONDITION_FROM_REFUSED_LIST': '#20c997',
      'APPROVAL_POLICY_MODE_UPDATE': '#0d6efd',
      'CREDOLAB_THRESHOLDS_UPDATE': '#6610f2',
      'CREDOLAB_FLAG_UPDATE': '#6f42c1'
    };
    return colors[code] || '#6c757d';
  }

  /**
   * Formatear mensaje de log según el código y parámetros
   */
  formatLogMessage(code: string, params: any, executiveName: string): string {
    switch (code) {
      case 'AGE_POLITICS':
        return this.formatAgePolicyMessage(params);
      case 'GROUP_AGE_POLICY_UPDATE':
        return this.formatGroupAgePolicyMessage(params);  // NUEVO
      case 'ADDED_WORK_CONDITION':
        return `➕ Agregó condición laboral: "${params.newCondition}"`;
      case 'ADDED_WORK_CONDITION_TO_GROUP':
        return `➕ Agregó condición "${params.newCondition}" al grupo "${params.groupName}"`;
      case 'REMOVED_WORK_CONDITION_FROM_GROUP':
        return `➖ Removió condición "${params.removedCondition}" del grupo "${params.groupName}"`;
      case 'CREATED_APPROVAL_GROUP':
        return `➕ Creó grupo "${params.name}" con límites $${this.formatCurrency(params.minIncome)} - $${this.formatCurrency(params.maxIncome)}`;
      case 'UPDATED_APPROVAL_GROUP_INCOME_LIMITS':
        return this.formatIncomeLimitsMessage(params);
      case 'DELETED_APPROVAL_GROUP':
        return `➖ Eliminó grupo de aprobación "${params.name}"`;
      case 'ADDED_WORK_CONDITION_TO_REFUSED_LIST':
        return `🚫 Agregó "${params.newCondition}" a condiciones rechazadas`;
      case 'REMOVED_WORK_CONDITION_FROM_REFUSED_LIST':
        return `✅ Removió "${params.removedCondition}" de condiciones rechazadas`;
      case 'APPROVAL_POLICY_MODE_UPDATE':
        return `🎯 Cambió el modo de política: <span class="old-values">${params.oldMode ?? '—'}</span> <i class="fas fa-arrow-right"></i> <span class="new-values">${params.newMode}</span>`;
      case 'CREDOLAB_THRESHOLDS_UPDATE':
        return this.formatCredolabThresholdsMessage(params);
      case 'CREDOLAB_FLAG_UPDATE':
        return `🚩 Flag <strong>${params.flagName}</strong>: <span class="old-values">${params.oldValue ?? '—'}</span> <i class="fas fa-arrow-right"></i> <span class="new-values">${params.newValue}</span>`;
      default:
        return `Realizó una acción de tipo ${code}`;
    }
  }

  /**
   * Formatea cambios de umbrales Credolab. Backend emite { oldValues, newValues }
   * con sólo las claves que efectivamente cambiaron (minScore / maxProbability / requireBoth).
   */
  private formatCredolabThresholdsMessage(params: any): string {
    const oldV = params?.oldValues || {};
    const newV = params?.newValues || {};
    const changes = Object.keys(newV).map(key => {
      const from = oldV[key] ?? '—';
      const to = newV[key];
      return `${key}: <span class="old-values">${from}</span> <i class="fas fa-arrow-right"></i> <span class="new-values">${to}</span>`;
    });
    return changes.length
      ? `🔵 Actualizó umbrales Credolab — ${changes.join(', ')}`
      : '🔵 Actualizó umbrales Credolab';
  }

  /**
   * Formatear mensaje específico para cambios de política de edad
   */
  private formatAgePolicyMessage(params: any): string {
    const oldMin = params.oldMin;
    const oldMax = params.oldMax;
    const newMin = params.newMin;
    const newMax = params.newMax;

    return `Modificó rango de edad: <span class="old-values">${oldMin}-${oldMax} años</span> <i class="fas fa-arrow-right"></i> <span class="new-values">${newMin}-${newMax} años</span>`;
  }

  /**
   * =====================================================
   * NUEVO: Formatear mensaje para cambios de edad por grupo
   * =====================================================
   */
  private formatGroupAgePolicyMessage(params: any): string {
    const groupName = params.groupName || 'Sin nombre';
    const oldMinAge = params.oldMinAge;
    const oldMaxAge = params.oldMaxAge;
    const newMinAge = params.newMinAge;
    const newMaxAge = params.newMaxAge;

    return `🎂 Modificó los límites de edad del grupo <strong>"${groupName}"</strong>: <span class="old-values">[${oldMinAge} - ${oldMaxAge}]</span> <i class="fas fa-arrow-right"></i> <span class="new-values">[${newMinAge} - ${newMaxAge}]</span>`;
  }

  /**
   * Formatear mensaje específico para cambios de límites de ingresos
   */
  private formatIncomeLimitsMessage(params: any): string {
    const groupName = params.name;
    const oldMin = params.oldMinIncome;
    const oldMax = params.oldMaxIncome;
    const newMin = params.newMinIncome || params.minIncome;
    const newMax = params.newMaxIncome || params.maxIncome;

    return `Actualizó límites del grupo "${groupName}": <span class="old-values">$${this.formatCurrency(oldMin)} - $${this.formatCurrency(oldMax)}</span> <i class="fas fa-arrow-right"></i> <span class="new-values">$${this.formatCurrency(newMin)} - $${this.formatCurrency(newMax)}</span>`;
  }

  /**
   * Formatear números como moneda
   */
  private formatCurrency(value: number): string {
    return value ? value.toLocaleString() : '0';
  }

  /**
   * Agrupar logs por código
   */
  groupLogsByCode = (rawLogs: any[]): any[] => {
    const grouped: { [key: string]: any } = {};

    rawLogs.forEach(log => {
      const code = log.code;
      
      if (!grouped[code]) {
        grouped[code] = {
          code: code,
          label: this.getCodeLabel(code),
          color: this.getCodeColor(code),
          records: []
        };
      }


      const formattedRecord = {
        id: log._id,
        date: log.date || log.createdAt || log.updatedAt,
        executiveName: log.executiveName,
        personId: log.person,
        message: this.formatLogMessage(code, log.params, log.executiveName),
        params: log.params,
        createdAt: log.createdAt || log.date
      };

      grouped[code].records.push(formattedRecord);
    });

    // Convertir a array y ordenar
    const groupedArray = Object.values(grouped);

    // Nota: El backend ya viene ordenado por _id descendente (más recientes primero)
    // pero ordenamos adicionalmente por fecha formateada para asegurar consistencia
    groupedArray.forEach(group => {
      group.records.sort((a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    });

    // Ordenar grupos por fecha del registro más reciente
    groupedArray.sort((a: any, b: any) => {
      const dateA = a.records[0] ? new Date(a.records[0].date).getTime() : 0;
      const dateB = b.records[0] ? new Date(b.records[0].date).getTime() : 0;
      return dateB - dateA;
    });

    return groupedArray;
  };
}
